import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";
import { CREATIVE_FORMATS, type CreativeBriefOutput, type CreativeReviewOutput } from "@shared/creative.ts";
import { PLATFORMS } from "@shared/content.ts";

export default function Creative() {
  const { activeBrand } = useBrands();
  const [tab, setTab] = useState<"brief" | "review">("brief");
  return (
    <div className="page">
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">ออกแบบครีเอทีฟ <span className="mh-muted mh-eng" style={{ fontSize: 15 }}>Creative Studio</span></div>
          <div className="page__subtitle">{activeBrand ? <>บริบท: <b>{activeBrand.name}</b></> : "เลือกแบรนด์ก่อน"}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className={"mh-btn mh-btn--sm " + (tab === "brief" ? "mh-btn--primary" : "mh-btn--ghost")} onClick={() => setTab("brief")}>Creative Brief</button>
          <button className={"mh-btn mh-btn--sm " + (tab === "review" ? "mh-btn--primary" : "mh-btn--ghost")} onClick={() => setTab("review")}>ตรวจครีเอทีฟ</button>
        </div>
      </div>
      {tab === "brief" ? <BriefTab brandId={activeBrand?.id ?? null} /> : <ReviewTab brandId={activeBrand?.id ?? null} />}
    </div>
  );
}

function BriefTab({ brandId }: { brandId: string | null }) {
  const [f, setF] = useState<Record<string, string>>({ platform: "Facebook", format: CREATIVE_FORMATS[0] });
  const [out, setOut] = useState<CreativeBriefOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { load(); }, [brandId]);
  async function load() { if (!brandId) return setHistory([]); const r = await api.get<{ projects: any[] }>(`/creative?brandId=${brandId}`); setHistory(r.projects); }

  async function gen() {
    if (!brandId) { setErr("เลือกแบรนด์ก่อน"); return; }
    setErr(null); setLoading(true); setOut(null);
    try { const r = await api.post<any>("/creative/brief", { brandId, inputs: f }); if (r.error === "ai_unparsable") { setErr("ลองใหม่"); return; } setOut(r.output); setIsMock(r.isMock); load(); }
    catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); } finally { setLoading(false); }
  }
  async function open(id: string) { setLoading(true); try { const r = await api.get<{ project: any }>(`/creative/${id}`); setOut(r.project.data); setIsMock(false); } finally { setLoading(false); } }

  return (
    <>
      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <div className="form-grid">
          <div className="mh-field" style={{ margin: 0 }}><label className="mh-label">วัตถุประสงค์</label><input className="mh-input" value={f.objective || ""} onChange={(e) => set("objective", e.target.value)} placeholder="เช่น Lead / ขาย" /></div>
          <div className="mh-field" style={{ margin: 0 }}><label className="mh-label">แพลตฟอร์ม</label><select className="mh-select" value={f.platform} onChange={(e) => set("platform", e.target.value)}>{PLATFORMS.map((p) => <option key={p}>{p}</option>)}</select></div>
          <div className="mh-field" style={{ margin: 0 }}><label className="mh-label">รูปแบบ</label><select className="mh-select" value={f.format} onChange={(e) => set("format", e.target.value)}>{CREATIVE_FORMATS.map((p) => <option key={p}>{p}</option>)}</select></div>
          <div className="mh-field" style={{ margin: 0 }}><label className="mh-label">Audience</label><input className="mh-input" value={f.audience || ""} onChange={(e) => set("audience", e.target.value)} /></div>
          <div className="mh-field" style={{ margin: 0, gridColumn: "1 / -1" }}><label className="mh-label">Offer</label><input className="mh-input" value={f.offer || ""} onChange={(e) => set("offer", e.target.value)} /></div>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={gen} disabled={loading}>{loading ? <span className="spinner" /> : <><Icon name="Image" size={18} /> สร้าง Creative Brief</>}</button>
          {history.length > 0 && <select className="mh-select" style={{ width: "auto" }} defaultValue="" onChange={(e) => e.target.value && open(e.target.value)}><option value="">📁 เดิม ({history.length})</option>{history.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}</select>}
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>

      {loading && <div className="empty"><span className="spinner spinner--dark" /></div>}
      {out && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>Brief จาก <b>Mock</b> — ใส่ Claude Key เพื่อไอเดียครีเอทีฟจริง</div></div>}
          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <KV k="Creative Strategy" v={out.creativeStrategy} />
            <div style={{ background: "var(--blue-050)", borderRadius: 10, padding: 12, margin: "10px 0" }}>
              <div className="mh-muted" style={{ fontSize: 12 }}>Hook</div><div style={{ fontWeight: 700, fontSize: 16 }}>{out.hook}</div>
              <div className="mh-muted" style={{ fontSize: 12, marginTop: 8 }}>Headline</div><div style={{ fontWeight: 600 }}>{out.headline}</div>
              <div className="mh-sec" style={{ fontSize: 13.5 }}>{out.subheadline}</div>
            </div>
            <div className="form-grid" style={{ gap: "4px 16px" }}>
              <KV k="Proof" v={out.proof} /><KV k="CTA" v={out.cta} /><KV k="Key Visual" v={out.keyVisual} /><KV k="Composition" v={out.composition} />
              <KV k="Layout" v={out.layout} /><KV k="Visual Hierarchy" v={out.visualHierarchy} /><KV k="สี" v={out.color} /><KV k="ฟอนต์" v={out.font} />
            </div>
            <div style={{ background: "var(--gray-050)", borderRadius: 8, padding: 12, marginTop: 8 }}>
              <div className="mh-muted" style={{ fontSize: 12 }}>🎨 AI Image Prompt</div><div style={{ fontFamily: "var(--font-en)", fontSize: 13 }}>{out.aiImagePrompt}</div>
              <div className="mh-muted" style={{ fontSize: 12, marginTop: 8 }}>Negative Prompt</div><div style={{ fontFamily: "var(--font-en)", fontSize: 13 }}>{out.negativePrompt}</div>
            </div>
          </div>
          <h3 style={{ fontSize: 15, margin: "14px 0 10px" }}>มุมครีเอทีฟ ({out.angles.length})</h3>
          <div className="cards">
            {out.angles.map((a, i) => (
              <div className="mh-card card-pad" key={i}>
                <div className="mh-spread"><b>{a.name}</b></div>
                <div style={{ fontSize: 13.5, marginTop: 6 }}><span className="mh-muted">Hook:</span> {a.hook}</div>
                <div style={{ fontSize: 13.5 }}><span className="mh-muted">Message:</span> {a.message}</div>
                <div style={{ fontSize: 13.5 }}><span className="mh-muted">Visual:</span> {a.visual}</div>
                <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>👥 {a.bestAudience} · 🎯 {a.cta}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function ReviewTab({ brandId }: { brandId: string | null }) {
  const [desc, setDesc] = useState("");
  const [out, setOut] = useState<CreativeReviewOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function review() {
    if (!desc.trim()) { setErr("อธิบายชิ้นงานก่อน"); return; }
    setErr(null); setLoading(true); setOut(null);
    try { const r = await api.post<any>("/creative/review", { brandId, description: desc }); if (r.error === "ai_unparsable") { setErr("ลองใหม่"); return; } setOut(r.output); setIsMock(r.isMock); }
    catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); } finally { setLoading(false); }
  }

  return (
    <>
      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <label className="mh-label">อธิบายชิ้นงานครีเอทีฟ (Hook, Headline, ภาพ, CTA, สี ฯลฯ)</label>
        <textarea className="mh-textarea" style={{ minHeight: 130 }} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="เช่น: ภาพรถ SUV พื้นหลังภูเขา พาดหัว 'รถบ้านสภาพดี' มีปุ่มทักไลน์สีเขียว ข้อความเยอะพอควร..." />
        <div className="mh-help" style={{ marginTop: 6 }}>💡 ตอนนี้ Mock ยังดูภาพไม่ได้ — อธิบายเป็นข้อความ (ใส่ Claude Key + วิชัน จะดูภาพจริงได้)</div>
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={review} disabled={loading}>{loading ? <span className="spinner" /> : <><Icon name="Radar" size={18} /> ตรวจให้คะแนน</>}</button>
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>
      {loading && <div className="empty"><span className="spinner spinner--dark" /></div>}
      {out && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>รีวิวจาก <b>Mock</b> (จากคำอธิบาย) — ใส่ Claude Key + วิชันเพื่อดูภาพจริง</div></div>}
          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <div className="mh-spread"><h3 style={{ fontSize: 15 }}>คะแนนรวม</h3><div style={{ fontSize: 30, fontWeight: 800, color: out.score >= 75 ? "var(--green-600)" : out.score >= 55 ? "var(--amber-600)" : "var(--red-600)" }}>{out.score}<span style={{ fontSize: 16 }}>/100</span></div></div>
            <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
              {out.checks.map((c, i) => (
                <div key={i} className="mh-row" style={{ gap: 10 }}>
                  <span style={{ minWidth: 100, fontSize: 13.5 }}>{c.item}</span>
                  <div style={{ flex: 1, height: 7, background: "var(--gray-100)", borderRadius: 6, overflow: "hidden" }}><div style={{ width: `${c.score * 10}%`, height: "100%", background: c.score >= 7 ? "var(--green-600)" : c.score >= 5 ? "var(--amber-600)" : "var(--red-600)" }} /></div>
                  <span className="mh-muted" style={{ fontSize: 12.5, minWidth: 150 }}>{c.note}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="form-grid" style={{ alignItems: "start" }}>
            <FixCard title="🔴 ต้องแก้ (Critical)" items={out.criticalIssues} tone="problem" />
            <FixCard title="🟡 ควรแก้ (Important)" items={out.importantFixes} tone="watch" />
          </div>
          <div className="mh-card card-pad" style={{ marginTop: 14 }}>
            <KV k="✏️ ลองเขียนใหม่ (Rewrite)" v={out.rewrite} />
            <KV k="📐 Layout ที่แนะนำ" v={out.recommendedLayout} />
          </div>
        </>
      )}
    </>
  );
}

function KV({ k, v }: { k: string; v: string }) { return <div style={{ marginBottom: 8 }}><div className="mh-muted" style={{ fontSize: 12.5 }}>{k}</div><div style={{ fontSize: 14 }}>{v}</div></div>; }
function FixCard({ title, items, tone }: { title: string; items: string[]; tone: string }) {
  return <div className="mh-card card-pad"><h3 style={{ fontSize: 14, marginBottom: 8 }}>{title}</h3>{items.length ? <ul style={{ margin: 0, paddingLeft: 18 }}>{items.map((x, i) => <li key={i} style={{ marginBottom: 4, fontSize: 13.8 }}>{x}</li>)}</ul> : <span className={"mh-pill mh-pill--winner"}>ผ่าน</span>}</div>;
}
